import { useEffect, useRef, useState } from "react";
import type { Person } from "../../types";
import type { RoleTypeOption } from "../../config/peopleRoleTypes";
import type { RoleListSource } from "../../config/peopleRoleTypes";
import { ACTIVITY_ROLE_TYPES, getRoleLabel } from "../../config/peopleRoleTypes";

const OTHER_ROLE = "__other__";

/** True when `role` isn't one of the listed choices — a named "Other…" role. */
function isUnlistedRole(role: string, roleTypes: RoleTypeOption[]): boolean {
  return role === "other" || !roleTypes.some((t) => t.value === role && t.value !== "other");
}

/**
 * A role dropdown whose "Other…" asks what the role actually is, the way a
 * custom event asks for its name. The typed name is stored as the role itself
 * ("Purser"), so it reads right wherever the person is shown; the bare legacy
 * "other" still means "unnamed".
 */
export function RoleSelect({
  value,
  onChange,
  roleTypes,
}: {
  value: string;
  onChange: (role: string) => void;
  roleTypes: RoleTypeOption[];
}) {
  const listed = roleTypes.filter((t) => t.value !== "other");
  const custom = isUnlistedRole(value, roleTypes);
  const inputRef = useRef<HTMLInputElement>(null);
  const [focusCustom, setFocusCustom] = useState(false);
  useEffect(() => {
    if (focusCustom) {
      inputRef.current?.focus();
      setFocusCustom(false);
    }
  }, [focusCustom]);

  return (
    <>
      <select
        className="form-input people-role-select"
        value={custom ? OTHER_ROLE : value}
        onChange={(e) => {
          if (e.target.value === OTHER_ROLE) {
            onChange("other");
            setFocusCustom(true);
          } else {
            onChange(e.target.value);
          }
        }}
      >
        {listed.map((t) => (
          <option key={t.value} value={t.value}>{t.label}</option>
        ))}
        <option value={OTHER_ROLE}>Other…</option>
      </select>
      {custom && (
        <input
          ref={inputRef}
          type="text"
          className="form-input people-role-custom"
          value={value === "other" ? "" : value}
          onChange={(e) => onChange(e.target.value || "other")}
          placeholder="Role"
        />
      )}
    </>
  );
}

interface PeopleEditorProps {
  people: Person[];
  onChange: (people: Person[]) => void;
  roleTypes?: RoleTypeOption[];
  label?: string;
}

export function PeopleEditor({ people, onChange, roleTypes = ACTIVITY_ROLE_TYPES, label = "People" }: PeopleEditorProps) {
  const handleAdd = () => {
    onChange([...people, { role: roleTypes[0]?.value || "other", name: "" }]);
  };

  const handleRemove = (index: number) => {
    onChange(people.filter((_, i) => i !== index));
  };

  const handleChange = (index: number, field: keyof Person, value: string) => {
    onChange(people.map((p, i) => (i === index ? { ...p, [field]: value } : p)));
  };

  return (
    <div className="custom-response-section">
      <div className="section-header">
        <span className="form-label">{label}</span>
        <button type="button" className="btn btn-small btn-secondary" onClick={handleAdd}>
          + Add
        </button>
      </div>
      {people.map((person, i) => (
        <div key={i} className="people-row">
          <RoleSelect
            value={person.role}
            onChange={(role) => handleChange(i, "role", role)}
            roleTypes={roleTypes}
          />
          <input
            type="text"
            className="form-input"
            value={person.name}
            onChange={(e) => handleChange(i, "name", e.target.value)}
            placeholder="Name"
          />
          <button type="button" className="btn-icon" onClick={() => handleRemove(i)} title="Remove">
            &times;
          </button>
        </div>
      ))}
    </div>
  );
}

export type RoleListScope = "type" | "place";

/** What the people form needs to let its role list be edited. */
export interface RoleListEditing {
  /** The activity's type, as read ("Transportation"); null when untyped. */
  typeLabel: string | null;
  /** The linked place's name; null when there's none. */
  placeName: string | null;
  /** Where the roles showing now came from. */
  source: RoleListSource;
  hasTypeList: boolean;
  hasPlaceList: boolean;
  /** Save a list for that scope; null goes back to the default under it. */
  onSave: (scope: RoleListScope, roles: RoleTypeOption[] | null) => void;
}

/**
 * Roles as chips to keep or drop, plus a box to type a new one in. Typing one
 * that's already there (in any case) just turns it on.
 */
export function RoleChecklist({
  options,
  onOptionsChange,
  kept,
  onKeptChange,
}: {
  options: RoleTypeOption[];
  onOptionsChange: (options: RoleTypeOption[]) => void;
  kept: Set<string>;
  onKeptChange: (kept: Set<string>) => void;
}) {
  const [newRole, setNewRole] = useState("");

  const toggle = (value: string) => {
    const next = new Set(kept);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    onKeptChange(next);
  };

  const addRole = () => {
    const label = newRole.trim();
    if (!label) return;
    const existing = options.find((t) => t.label.toLowerCase() === label.toLowerCase());
    const value = existing?.value ?? label;
    if (!existing) onOptionsChange([...options, { value, label }]);
    onKeptChange(new Set(kept).add(value));
    setNewRole("");
  };

  return (
    <>
      <div className="activity-people-roles">
        {options.map((t) => (
          <button
            key={t.value}
            type="button"
            className={`role-chip${kept.has(t.value) ? " on" : ""}`}
            aria-pressed={kept.has(t.value)}
            onClick={() => toggle(t.value)}
          >
            {t.label}
          </button>
        ))}
      </div>
      <div className="role-list-add">
        <input
          type="text"
          className="form-input"
          value={newRole}
          onChange={(e) => setNewRole(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              addRole();
            }
          }}
          placeholder="Add a role, e.g. Purser"
        />
        <button type="button" className="btn btn-small btn-secondary" onClick={addRole} disabled={!newRole.trim()}>
          Add
        </button>
      </div>
    </>
  );
}

/**
 * Choose which roles an activity offers up front: tap to keep or drop one, type
 * a new one in, and say whether it's for every activity of this type or just
 * this place. Lives inside the people form, where the long list is noticed.
 */
function RoleListEditor({
  roleTypes,
  featuredRoles,
  editing,
  onDone,
}: {
  roleTypes: RoleTypeOption[];
  featuredRoles: string[] | null;
  editing: RoleListEditing;
  onDone: () => void;
}) {
  const pickable = roleTypes.filter((t) => t.value !== "other");
  const [options, setOptions] = useState<RoleTypeOption[]>(pickable);
  const [kept, setKept] = useState<Set<string>>(
    () => new Set(featuredRoles ?? pickable.map((t) => t.value)),
  );
  const canType = editing.typeLabel !== null;
  const canPlace = editing.placeName !== null;
  const [scope, setScope] = useState<RoleListScope>(
    editing.source === "place" || !canType ? "place" : "type",
  );
  const save = () => {
    // Kept roles in the order they're shown, newly typed ones last.
    editing.onSave(scope, options.filter((t) => kept.has(t.value)));
    onDone();
  };

  const canReset = scope === "place" ? editing.hasPlaceList : editing.hasTypeList;

  return (
    <div className="role-list-editor">
      <p className="who-here-hint">Tap the roles to offer up front. The rest stay under More….</p>
      <RoleChecklist options={options} onOptionsChange={setOptions} kept={kept} onKeptChange={setKept} />
      {canType && canPlace && (
        <div className="role-list-scope">
          <span className="who-here-hint">Use this list for</span>
          <div className="activity-people-roles">
            <button
              type="button"
              className={`role-chip${scope === "type" ? " on" : ""}`}
              aria-pressed={scope === "type"}
              onClick={() => setScope("type")}
            >
              Every {editing.typeLabel} activity
            </button>
            <button
              type="button"
              className={`role-chip${scope === "place" ? " on" : ""}`}
              aria-pressed={scope === "place"}
              onClick={() => setScope("place")}
            >
              Just {editing.placeName}
            </button>
          </div>
        </div>
      )}
      {!(canType && canPlace) && (
        <p className="who-here-hint">
          {scope === "type"
            ? `Saved for every ${editing.typeLabel} activity.`
            : `Saved for ${editing.placeName}.`}
        </p>
      )}
      <div className="form-actions">
        <button type="button" className="btn btn-small" onClick={save} disabled={kept.size === 0}>
          Save list
        </button>
        <button type="button" className="btn btn-secondary btn-small" onClick={onDone}>
          Cancel
        </button>
        {canReset && (
          <button
            type="button"
            className="btn-link role-list-reset"
            onClick={() => {
              editing.onSave(scope, null);
              onDone();
            }}
          >
            {scope === "place" ? `Use the ${editing.typeLabel ?? "default"} list` : "Reset to default"}
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * The staff and crew met on an activity, edited in place on its detail page:
 * tap "+ Add" (or a person) and pick a role from chips — the ones that fit
 * this kind of activity come first — or name one under "Other…". Each change
 * is handed up to be saved straight away; there's no form to open.
 */
export function ActivityPeople({
  people,
  roleTypes,
  onAdd,
  onUpdate,
  onRemove,
  featuredRoles = null,
  roleListEditing,
  openAddSignal = 0,
  label = "Staff & crew",
}: {
  people: Person[];
  roleTypes: RoleTypeOption[];
  /**
   * The role values to show up front; the rest of `roleTypes` waits behind a
   * "More…" chip. Null shows them all.
   */
  featuredRoles?: string[] | null;
  /** Lets the role list itself be edited from the form; omitted, it can't be. */
  roleListEditing?: RoleListEditing;
  onAdd: (person: Person) => void;
  onUpdate: (index: number, person: Person) => void;
  onRemove: (index: number) => void;
  /** Bump to open the add form and bring it into view (the ⋮ menu's "Add person"). */
  openAddSignal?: number;
  label?: string;
}) {
  // null: closed; "new": adding; a number: editing that person.
  const [editing, setEditing] = useState<"new" | number | null>(null);
  const [role, setRole] = useState("");
  const [customRole, setCustomRole] = useState(false);
  const [name, setName] = useState("");
  const [showAllRoles, setShowAllRoles] = useState(false);
  const [editingList, setEditingList] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  const nameRef = useRef<HTMLInputElement>(null);
  const customRef = useRef<HTMLInputElement>(null);

  const openAdd = () => {
    setShowAllRoles(false);
    setEditing("new");
    setRole("");
    setCustomRole(false);
    setName("");
  };

  const openEdit = (index: number) => {
    const p = people[index];
    if (!p) return;
    setShowAllRoles(false);
    const unlisted = isUnlistedRole(p.role, roleTypes);
    setEditing(index);
    setCustomRole(unlisted);
    setRole(unlisted && p.role === "other" ? "" : p.role);
    setName(p.name);
  };

  useEffect(() => {
    if (openAddSignal === 0) return;
    setShowAllRoles(false);
    setEditing("new");
    setRole("");
    setCustomRole(false);
    setName("");
  }, [openAddSignal]);

  // The form opens below the people already listed, often below the fold —
  // bring it into view whenever it opens, from "+ Add", a tap or the menu.
  useEffect(() => {
    if (editing === null) return;
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [editing]);

  const close = () => {
    setEditing(null);
    setEditingList(false);
  };

  // Lead with the roles that fit, plus any already used here and the one
  // picked, so a long general list doesn't bury them.
  const pickable = roleTypes.filter((t) => t.value !== "other");
  const featured = featuredRoles ? new Set([...featuredRoles, ...people.map((p) => p.role)]) : null;
  const shownRoles =
    featured && !showAllRoles
      ? pickable.filter((t) => featured.has(t.value) || (!customRole && t.value === role))
      : pickable;
  const hiddenCount = pickable.length - shownRoles.length;

  const trimmedRole = role.trim();
  const trimmedName = name.trim();
  const canSave = trimmedName !== "" && (customRole ? trimmedRole !== "" : role !== "");

  const save = () => {
    if (!canSave || editing === null) return;
    const person: Person = { role: trimmedRole, name: trimmedName };
    if (editing === "new") onAdd(person);
    else onUpdate(editing, person);
    close();
  };

  const pickRole = (value: string) => {
    setCustomRole(false);
    setRole(value);
    nameRef.current?.focus();
  };

  const pickOther = () => {
    setCustomRole(true);
    if (!isUnlistedRole(role, roleTypes)) setRole("");
    setTimeout(() => customRef.current?.focus(), 0);
  };

  return (
    <div className="activity-people">
      <div className="who-here-head">
        <span className="who-here-label">{label}</span>
        {editing === null && (
          <button type="button" className="btn btn-small btn-secondary activity-people-add" onClick={openAdd}>
            + Add
          </button>
        )}
      </div>

      {people.length > 0 && (
        <div className="people-list">
          {people.map((p, i) => (
            <button
              key={i}
              type="button"
              className={`people-item people-item-btn${editing === i ? " on" : ""}`}
              onClick={() => (editing === i ? close() : openEdit(i))}
              title="Edit"
            >
              <span className="people-role">{getRoleLabel(p.role, roleTypes)}</span>
              <span className="people-name">{p.name}</span>
            </button>
          ))}
        </div>
      )}
      {people.length === 0 && editing === null && (
        <p className="who-here-hint">Note the pilot, your server, the guide — anyone you met here.</p>
      )}

      {editing !== null && (
        <form
          ref={formRef}
          className="activity-people-form"
          onSubmit={(e) => {
            e.preventDefault();
            save();
          }}
        >
          {editingList && roleListEditing ? (
            <RoleListEditor
              roleTypes={roleTypes}
              featuredRoles={featuredRoles}
              editing={roleListEditing}
              onDone={() => setEditingList(false)}
            />
          ) : (
            <>
              <div className="activity-people-roles">
                {shownRoles.map((t) => (
                  <button
                    key={t.value}
                    type="button"
                    className={`role-chip${!customRole && role === t.value ? " on" : ""}`}
                    aria-pressed={!customRole && role === t.value}
                    onClick={() => pickRole(t.value)}
                  >
                    {t.label}
                  </button>
                ))}
                {hiddenCount > 0 && (
                  <button type="button" className="role-chip role-chip-more" onClick={() => setShowAllRoles(true)}>
                    More…
                  </button>
                )}
                <button
                  type="button"
                  className={`role-chip${customRole ? " on" : ""}`}
                  aria-pressed={customRole}
                  onClick={pickOther}
                >
                  Other…
                </button>
                {roleListEditing && (
                  <button type="button" className="btn-link role-list-edit" onClick={() => setEditingList(true)}>
                    Edit list
                  </button>
                )}
              </div>
              {customRole && (
                <input
                  ref={customRef}
                  type="text"
                  className="form-input"
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                  placeholder="Role, e.g. Purser"
                />
              )}
              <input
                ref={nameRef}
                type="text"
                className="form-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Name"
                autoFocus={editing !== "new"}
              />
              <div className="form-actions">
                <button type="submit" className="btn btn-small" disabled={!canSave}>
                  {editing === "new" ? "Add" : "Save"}
                </button>
                <button type="button" className="btn btn-secondary btn-small" onClick={close}>
                  Cancel
                </button>
                {typeof editing === "number" && (
                  <button
                    type="button"
                    className="btn btn-small btn-secondary activity-people-remove"
                    onClick={() => {
                      onRemove(editing);
                      close();
                    }}
                  >
                    Remove
                  </button>
                )}
              </div>
            </>
          )}
        </form>
      )}
    </div>
  );
}

/** Read-only display of people */
export function PeopleDisplay({
  people,
  roleTypes = ACTIVITY_ROLE_TYPES,
  label = "People",
}: {
  people: Person[];
  roleTypes?: RoleTypeOption[];
  label?: string;
}) {
  if (people.length === 0) return null;

  return (
    <div className="custom-response-display">
      <h4>{label}</h4>
      <div className="people-list">
        {people.map((p, i) => (
          <div key={i} className="people-item">
            <span className="people-role">{getRoleLabel(p.role, roleTypes)}</span>
            <span className="people-name">{p.name}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
